import { notification } from "antd";
import {
  setAllUsers,
  setChannelMessages,
  setChatUsers,
  setIsAllUsersLoading,
  setIsChannelMessagesLoading,
  setIsChatUsersLoading,
  setSelectedUser,
  setReactionUsers,
  setPendingReaction,
  clearPendingReaction,
} from "../../store/slices/chatsSlice";
import {
  apiUrl,
  collection_ref,
  constant,
  message_type,
  roles as salonRoles,
} from "../../utils/constants";
import { signInWithCustomToken } from "firebase/auth";
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  runTransaction,
} from "firebase/firestore";
import { ToastCrossIcon } from "../../constants/svg";
import { auth, db } from "../../firebase";
import {
  GetAuthUserLocalStorage,
  GetTokenLocalStorage,
} from "../localStorage/localStorage";
import { shouldClearPendingReaction } from "../../utils/reactions";

export const isSuperAdmin =
  GetAuthUserLocalStorage()?.roles[0]?.id == salonRoles.super_admin;

export const isUserDeleted = (user, members) => {
  if (user?.id == 1) return false;
  const member = members.find(
    (member) => member.id.toString() === user?.id?.toString(),
  );
  // If member is not found return true else return false
  return !member;
};

export const isChatUserDeleted = (user, members) => {
  const isDeleted = isSuperAdmin
    ? user?.is_deleted
    : isUserDeleted(user, members);
  return isDeleted;
};

// Helper function to convert Firebase Timestamps to serializable values
export const convertFirebaseTimestamps = (data) => {
  if (!data) return data;

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => convertFirebaseTimestamps(item));
  }

  // Handle objects
  if (typeof data === "object") {
    // Check if it's a Firebase Timestamp
    if (data.seconds !== undefined && data.nanoseconds !== undefined) {
      return {
        seconds: data.seconds,
        nanoseconds: data.nanoseconds,
      };
    }

    // Recursively convert all object properties
    const converted = {};
    for (const key in data) {
      converted[key] = convertFirebaseTimestamps(data[key]);
    }
    return converted;
  }

  // Return primitive values as-is
  return data;
};

let authUser = GetAuthUserLocalStorage();
let subscribed = null;

const clearMatchingPendingReactions = (messages, dispatch, getState) => {
  const state = getState();
  const pendingReactions = state.chats.pendingReactions;

  messages.forEach((message) => {
    const messagePending = pendingReactions[message.thread_id];
    if (!messagePending) return;

    Object.keys(messagePending).forEach((emoji) => {
      const emojiPending = messagePending[emoji];
      Object.keys(emojiPending).forEach((userId) => {
        const isAdding = emojiPending[userId];
        if (shouldClearPendingReaction(message, emoji, userId, isAdding)) {
          dispatch(
            clearPendingReaction({
              messageId: message.thread_id,
              emoji,
              userId,
            }),
          );
        }
      });
    });
  });
};

export const getAllUsers = async (dispatch, keyword = null, salon_id) => {
  salon_id =
    typeof authUser?.salon_id != "undefined" ? authUser?.salon_id : salon_id;

  if (typeof salon_id != "undefined") {
    try {
      dispatch(setIsAllUsersLoading(true));
      const usersCollectionRef = collection(db, collection_ref?.users);
      let userquerySnapshot;
      if (keyword && keyword.length > 3) {
        let queryData = query(
          usersCollectionRef,
          // where("salon_id", "==", salon_id),
          where("salon_ids", "array-contains", salon_id),
          where("name", "==", keyword),
        );

        userquerySnapshot = await getDocs(queryData);
      } else {
        let queryData = query(
          usersCollectionRef,
          // where("salon_id", "==", salon_id),
          where("salon_ids", "array-contains", salon_id),
        );
        userquerySnapshot = await getDocs(queryData);
      }

      const users = [];
      for (let j = 0; j < userquerySnapshot?.docs?.length; j++) {
        const doc = userquerySnapshot?.docs[j];
        const userData = doc.data();
        userData.id = doc.id;
        users.push(userData);
      }
      // Convert Firebase Timestamps before storing in Redux
      const sanitizedUsers = users.map((user) =>
        convertFirebaseTimestamps(user),
      );
      dispatch(setAllUsers(sanitizedUsers));
      dispatch(setIsAllUsersLoading(false));
    } catch (e) {
      notification.error({
        description: e.message,
        placement: "bottomRight",
        className: "createdNotification",
        closeIcon: <ToastCrossIcon />,
      });
      dispatch(setIsAllUsersLoading(false));
    }
  }
};

export const getFireBaseUserByID = async (id) => {
  const usersRef = collection(db, collection_ref?.users);
  const q = query(usersRef, where("id", "==", id.toString()));
  const querySnapshot = await getDocs(q);
  if (!querySnapshot.empty) {
    const userDoc = querySnapshot.docs[0];
    return userDoc.data();
  }
  return false;
};
export const updateFirebaseUser = async (user = null) => {
  let auth_id =
    typeof authUser?.id == "undefined"
      ? user?.id.toString()
      : authUser?.id.toString();
  const userDocRef = doc(db, collection_ref?.users, auth_id);
  const userData = (await getDoc(userDocRef)).data();
  if (!userData) {
    const usersCollection = doc(db, collection_ref?.users, auth_id);
    await setDoc(usersCollection, {
      name: authUser?.name,
      image: authUser?.image ? authUser?.image : "",
      online: true,
      id: authUser?.id.toString(),
      email: authUser?.email,
      position: authUser?.position_id,
    });
  } else {
    userData.online = true;
    if (user) {
      userData.name = user?.name;
      userData.image = user?.image ? user?.image : "";
      userData.position = user?.position_id;
    }
    await updateDoc(userDocRef, userData);
  }
};
export const updateOrganizationFirebase = async (user = null) => {
  const userDocRef = doc(db, collection_ref?.users, user.id);
  const userData = (await getDoc(userDocRef)).data();
  if (!userData) {
    const usersCollection = doc(db, collection_ref?.users, user.id);
    await setDoc(usersCollection, {
      name: user?.name,
      image: user?.image ? user?.image : "",
      online: false,
      id: user?.id.toString(),
      email: user?.email,
      position: user?.position_id,
    });
  } else {
    if (user) {
      userData.name = user?.name;
      userData.image = user?.image ? user?.image : "";
      userData.position = user?.position_id;
    }
    await updateDoc(userDocRef, userData);
  }
};
// Helper function to sort chats with pinned/starred chats first
const sortChatsWithPinnedFirst = (chats, currentUserId) => {
  const pinnedChats = [];
  const unpinnedChats = [];

  chats.forEach((chat) => {
    try {
      const pinnedUsers = chat.users_who_pinned_chat || [];
      const isPinned = pinnedUsers.includes(currentUserId);

      if (isPinned) {
        pinnedChats.push(chat);
      } else {
        unpinnedChats.push(chat);
      }
    } catch (e) {
      unpinnedChats.push(chat);
    }
  });

  // Sort both arrays by updated_at descending
  const compareByUpdatedAt = (a, b) => {
    const timestampA = a.updated_at;
    const timestampB = b.updated_at;

    if (!timestampA && !timestampB) return 0;
    if (!timestampA) return 1;
    if (!timestampB) return -1;

    // Handle Firebase Timestamp objects
    const timeA = timestampA?.seconds || timestampA?.toMillis?.() || timestampA;
    const timeB = timestampB?.seconds || timestampB?.toMillis?.() || timestampB;

    // Convert to comparable numbers
    const numA = typeof timeA === "number" ? timeA : timeA * 1000;
    const numB = typeof timeB === "number" ? timeB : timeB * 1000;

    return numB - numA; // Descending order
  };

  pinnedChats.sort(compareByUpdatedAt);
  unpinnedChats.sort(compareByUpdatedAt);

  return [...pinnedChats, ...unpinnedChats];
};

export const toggleStarChat = async (chatId, userId) => {
  try {
    const chatDocRef = doc(db, collection_ref?.chats, chatId);
    const chatDoc = await getDoc(chatDocRef);

    if (!chatDoc.exists()) {
      notification.error({
        description: "Chat not found",
        placement: "bottomRight",
        className: "createdNotification",
        closeIcon: <ToastCrossIcon />,
      });
      return false;
    }

    const chatData = chatDoc.data();
    const pinnedUsers = chatData.users_who_pinned_chat || [];
    const isPinned = pinnedUsers.includes(userId);

    if (isPinned) {
      // Unstar the chat
      await updateDoc(chatDocRef, {
        users_who_pinned_chat: arrayRemove(userId),
      });
      notification.success({
        message: "Chat unstarred",
        placement: "bottomRight",
        className: "createdNotification",
        closeIcon: <ToastCrossIcon />,
      });
    } else {
      // Star the chat
      await updateDoc(chatDocRef, {
        users_who_pinned_chat: arrayUnion(userId),
      });
      notification.success({
        message: "Chat starred",
        placement: "bottomRight",
        className: "createdNotification",
        closeIcon: <ToastCrossIcon />,
      });
    }

    return true;
  } catch (error) {
    console.error("Failed to update star status", error);
    notification.error({
      description: error.message || "Failed to update star status",
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
    return false;
  }
};

export const getChatUserList = (dispatch, auth_id) => {
  dispatch(setIsChatUsersLoading(true));

  const usersCollectionRef = collection(db, collection_ref?.users);
  const usersQuery = query(usersCollectionRef);

  const chatsCollectionRef = collection(db, collection_ref?.chats);
  const chatsQuery = query(
    chatsCollectionRef,
    where("users_ids", "array-contains", auth_id),
    ...(isSuperAdmin
      ? []
      : [where("salon_id", "==", parseInt(authUser?.salon?.id))]),
  );

  // helper to merge snapshots
  let chatUnsub = null;

  const userUnsub = onSnapshot(usersQuery, (usersSnap) => {
    // re-create chat subscription only once (or re-use existing)
    if (!chatUnsub) {
      chatUnsub = onSnapshot(chatsQuery, async () => {
        // ---- fetch latest chat list ordered ----
        const chatQuery = query(
          chatsCollectionRef,
          where("users_ids", "array-contains", auth_id),
          ...(isSuperAdmin
            ? []
            : [where("salon_id", "==", parseInt(authUser?.salon?.id))]),
          orderBy("updated_at", "desc"),
        );
        const chatData = await getDocs(chatQuery);

        const chats = [];
        chatData?.docs?.forEach((doc) => {
          const data = doc.data();
          data.chat_id = doc.id;
          data.is_user_group = !!data?.is_user_group;
          const [del] =
            data.deleted_statuses?.filter((d) => d.id == auth_id) || [];
          if (!del?.is_deleted) chats.push(data);
        });

        const users = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        chats.forEach((chat) => {
          const profiles = chat.users_ids
            .filter((id) => id != auth_id)
            .map((id) => users.find((u) => Number(u.id) === id))
            .filter(Boolean);
          chat.receiver_profile = profiles;
        });

        const sanitized = chats.map((c) => convertFirebaseTimestamps(c));

        // Sort chats with pinned/starred chats first
        const sortedChats = sortChatsWithPinnedFirst(sanitized, auth_id);

        dispatch(setChatUsers(sortedChats));
        dispatch(setIsChatUsersLoading(false));
      });
    }
  });

  // return cleanup combining both listeners
  return () => {
    userUnsub && userUnsub();
    chatUnsub && chatUnsub();
  };
};
export const getUnreadCount = async (setMessageCount) => {
  authUser = GetAuthUserLocalStorage();
  if (authUser?.id) {
    const chatsCollectionRef = collection(db, collection_ref?.chats);
    const chatsQuery = query(
      chatsCollectionRef,
      where("users_ids", "array-contains", authUser?.id),
    );

    await onSnapshot(chatsQuery, async (querySnapshot) => {
      let unreadCount = 0;
      querySnapshot.forEach((doc) => {
        const chatData = doc.data();
        chatData.chat_id = doc.id;
        const filteredData = chatData?.read_statuses?.filter(
          (item) => item.id == authUser?.id,
        );
        if (filteredData.length > 0 && !filteredData[0].is_read) {
          ++unreadCount;
        }
      });
      setMessageCount(unreadCount);
    });
  }
};
export const getChannelMessages = async (
  selectedUser,
  unsubscribe,
  dispatch,
  auth_id = null,
  getState = null,
) => {
  auth_id = typeof authUser?.id == "undefined" ? auth_id : authUser?.id;
  if (subscribed) {
    subscribed();
  }
  try {
    dispatch(setChannelMessages([]));
    // check if current user deleted messages
    const [currUserDeletedStatuses] =
      selectedUser && selectedUser.deleted_statuses
        ? selectedUser.deleted_statuses.filter((item) => item?.id == auth_id)
        : [];
    const isCurrentUserDeletedMsgs = currUserDeletedStatuses?.is_deleted;
    // Find the id of last message deleted by current user
    const [currUserDeletedMsgsIds] =
      selectedUser && selectedUser.deleted_messages_ids
        ? selectedUser.deleted_messages_ids.filter(
            (item) => item?.id == auth_id,
          )
        : "";
    const lastMsgId = currUserDeletedMsgsIds?.deleted_message_id;
    // if current user deleted messages than find the info of the deleted messages
    if (lastMsgId != "") {
      // finding the details of last messages deleted by current user
      dispatch(setIsChannelMessagesLoading(true));
      dispatch(setChannelMessages([]));

      /* const chatsDocRef = doc(db, collection_ref?.chats, selectedUser?.chat_id)
            const threadsCollectionRef = collection(chatsDocRef, collection_ref?.threads)
            const threadDocRef = doc(threadsCollectionRef, lastMsgId)
            const threadSnapshot = await getDoc(threadDocRef)
            const threadData = threadSnapshot.data() */

      // find all docs where created_at is > then created_at of last messages deleted by current user
      const messagesRef = collection(
        doc(db, collection_ref?.chats, selectedUser?.chat_id),
        collection_ref?.threads,
      );
      const sortedMessagesQuery = query(messagesRef, orderBy("created_at"));

      subscribed = onSnapshot(sortedMessagesQuery, (querySnapshot) => {
        const data = querySnapshot.docs.map((doc) => {
          const dt = convertFirebaseTimestamps(doc.data());
          dt.thread_id = doc.id;
          return dt;
        });
        let index = 0;
        for (let i = 0; i < data.length; i++) {
          if (data[i]?.thread_id == lastMsgId) {
            index = i;
          }
        }
        //                const index = data?.findIndex((item) => item?.thread_id == lastMsgId)
        const data_after_deleted = data?.slice(index + 1);

        if (getState)
          clearMatchingPendingReactions(data_after_deleted, dispatch, getState);
        dispatch(setChannelMessages(data_after_deleted));
        dispatch(setIsChannelMessagesLoading(false));
      });
    } else {
      // find all chat of current user
      dispatch(setIsChannelMessagesLoading(true));
      dispatch(setChannelMessages([]));

      const messagesRef = collection(
        doc(db, collection_ref?.chats, selectedUser?.chat_id),
        collection_ref?.threads,
      );
      const sortedMessagesQuery = query(messagesRef, orderBy("created_at"));

      subscribed = onSnapshot(sortedMessagesQuery, (querySnapshot) => {
        const data = querySnapshot.docs.map((doc) => {
          const dt = convertFirebaseTimestamps(doc.data());
          dt.thread_id = doc.id;
          return dt;
        });

        if (getState) clearMatchingPendingReactions(data, dispatch, getState);
        dispatch(setChannelMessages(data));
        dispatch(setIsChannelMessagesLoading(false));
      });
    }
    /* update unread to true */
    const chatsDocRef = doc(db, collection_ref?.chats, selectedUser?.chat_id);
    const chatData = (await getDoc(chatsDocRef)).data();
    const updatedArray = chatData?.read_statuses?.map((item) => {
      if (item.id == auth_id) {
        return {
          ...item,
          is_read: true,
        };
      } else {
        return item;
      }
    });
    if (updatedArray?.length > 0) {
      await updateDoc(chatsDocRef, {
        read_statuses: updatedArray,
      });
    }

    return subscribed;
  } catch (e) {
    notification.error({
      description: e.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
    dispatch(setIsChannelMessagesLoading(false));
  }
};

export const deleteThread = async (
  selectedUser,
  channelMessages,
  dispatch,
  onDelete,
  setOnDelete,
) => {
  try {
    const chatDocRef = doc(db, collection_ref?.chats, selectedUser?.chat_id);
    const chatDoc = await getDoc(chatDocRef);
    const lastMessage = channelMessages[channelMessages?.length - 1];

    if (chatDoc.exists()) {
      const data = chatDoc.data();
      const deleted_statuses = data?.deleted_statuses?.map((status) => {
        if (status?.id == authUser?.id) {
          return {
            id: authUser?.id,
            is_deleted: true,
          };
        } else {
          return status;
        }
      });

      const deleted_messages_ids = data?.deleted_messages_ids?.map((msg) => {
        if (msg?.id == authUser?.id) {
          return {
            id: authUser?.id,
            deleted_message_id: lastMessage?.thread_id || "",
          };
        } else {
          return msg;
        }
      });

      await updateDoc(chatDocRef, {
        deleted_statuses,
        deleted_messages_ids,
        updated_at: new Date(),
      });
      let tempSelectedUser = { ...selectedUser };
      tempSelectedUser.deleted_statuses = deleted_statuses;
      tempSelectedUser.deleted_messages_ids = deleted_messages_ids;

      dispatch(setSelectedUser(null));
      dispatch(setChannelMessages([]));
      setOnDelete(onDelete + 1);
    }
  } catch (e) {
    notification.error({
      description: e.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};

export const sendMessage = async (selectedUser, newMessage) => {
  try {
    const chatsDocRef = doc(db, collection_ref?.chats, selectedUser?.chat_id);
    const messagesRef = collection(chatsDocRef, collection_ref?.threads);
    let chatData = await getDoc(chatsDocRef);
    chatData = chatData.data();
    if (newMessage?.message_type == message_type.text) {
      if (newMessage?.text && newMessage?.text != "") {
        await addDoc(messagesRef, newMessage);
      }
    } else {
      await addDoc(messagesRef, newMessage);
    }
    let lastMessage = "";
    switch (newMessage?.message_type) {
      case message_type?.text:
        lastMessage = newMessage?.text;
        break;
      case message_type?.media:
      case message_type?.image:
      case message_type?.video:
      case message_type?.audio:
      case message_type?.pdf:
      case message_type?.media_collection:
        lastMessage = newMessage?.text || "Attachment";
        break;
      default:
        lastMessage = newMessage?.text || "Message";
        break;
    }
    let deleted_statuses = [];
    chatData?.users_ids.map((item) => {
      deleted_statuses.push({ id: item, is_deleted: false });
    });
    let read_statuses = [];
    chatData?.users_ids.map((item) => {
      read_statuses.push({ id: item, is_read: item == authUser?.id });
    });
    await updateDoc(chatsDocRef, {
      updated_at: newMessage?.created_at,
      last_message: lastMessage,
      deleted_statuses,
      read_statuses,
      salon_id: isSuperAdmin
        ? parseInt(selectedUser.salon_id)
        : parseInt(authUser?.salon?.id),
    });

    // Update last open time when user sends a message
    if (selectedUser?.chat_id && authUser?.id) {
      await updateLastOpenTime(selectedUser.chat_id, authUser.id);
    }
  } catch (e) {
    notification.error({
      description: e.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};

export const isUserAlreadyChatWithEachOther = async (
  curr_user_id,
  selected_user_id,
  dispatch,
  allUsers,
) => {
  try {
    let isAlreadyChat = false;
    let channel = null;
    let shouldBreak = false;
    let thread = null;
    // finds docs in which any one from curr_user_id or selected_user_id exist
    const q = query(
      collection(db, collection_ref?.chats),
      where("users_ids", "array-contains-any", [
        curr_user_id,
        selected_user_id,
      ]),
      where("is_group_chat", "==", false),
    );

    // check if users_ids contain curr_user_id and selected_user_id and users_ids.length == 2 (for 1-1 chat)
    // it means users already chat with each other else users are not already chat with each other
    const querySnapshot = await getDocs(q);
    querySnapshot?.docs?.forEach((doc) => {
      if (shouldBreak) {
      } else {
        const data = doc?.data();
        data.chat_id = doc.id;
        if (
          data?.users_ids?.includes(curr_user_id) &&
          data?.users_ids?.includes(selected_user_id) &&
          data?.users_ids.length == 2
        ) {
          thread = doc.id;
          isAlreadyChat = true;
          channel = data;
          shouldBreak = true; // break the loop if user already chat with each other
        } else {
          isAlreadyChat = false;
        }
      }
    });

    if (isAlreadyChat) {
      const usersRef = collection(db, collection_ref?.users);
      const q = query(usersRef, where("id", "==", selected_user_id.toString()));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        channel.receiver_profile = [convertFirebaseTimestamps(userDoc.data())];
        dispatch(setSelectedUser(convertFirebaseTimestamps(channel)));
      }
    } else {
      let salonId = authUser?.salon?.id;
      if (isSuperAdmin) {
        salonId = allUsers?.find(
          (user) => user.id == selected_user_id,
        )?.salon_id;
      }
      const chatRef = collection(db, collection_ref?.chats);
      const addedDocRef = await addDoc(chatRef, {
        salon_id: salonId,
        blocked_statuses: [
          { id: curr_user_id, is_blocked: false },
          { id: selected_user_id, is_blocked: false },
        ],
        created_at: serverTimestamp(),
        deleted_messages_ids: [
          {
            deleted_message_id: "",
            id: curr_user_id,
            is_group_message_deleted: false,
          },
          {
            deleted_message_id: "",
            id: selected_user_id,
            is_group_message_deleted: false,
          },
        ],
        deleted_statuses: [
          { id: curr_user_id, is_deleted: false },
          { id: selected_user_id, is_deleted: false },
        ],
        is_group_chat: false,
        is_mobile_group: false,
        last_message: "",
        read_statuses: [
          { id: curr_user_id, is_read: false },
          { id: selected_user_id, is_read: false },
        ],
        updated_at: serverTimestamp(),
        users_ids: [curr_user_id, selected_user_id],
        group_message_statuses: [
          { id: curr_user_id, is_group_message_deleted: false },
          { id: selected_user_id, is_group_message_deleted: false },
        ],
      });
      const newChatDocSnapshot = await getDoc(addedDocRef);
      const newChatData = newChatDocSnapshot.data();
      newChatData.chat_id = newChatDocSnapshot.id;
      thread = newChatDocSnapshot.id;
      isAlreadyChat = false;

      // find selected user profile
      const usersRef = collection(db, collection_ref?.users);
      const q = query(usersRef, where("id", "==", selected_user_id.toString()));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        newChatData.receiver_profile = [
          convertFirebaseTimestamps(userDoc.data()),
        ];
        dispatch(setSelectedUser(convertFirebaseTimestamps(newChatData)));
      }
    }
    return thread;
  } catch (e) {
    notification.error({
      description: e.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};

export const addUserToFirebase = async (data) => {
  try {
    await setDoc(doc(db, collection_ref?.users, data.id), data);
  } catch (e) {
    notification.error({
      description: e.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};

export const addUserToFirebaseOrReject = async (data) => {
  try {
    await Promise.race([
      setDoc(doc(db, collection_ref?.users, data.id), data),
      new Promise((resolve, reject) =>
        setTimeout(() => reject(new Error("Request timeout")), 20000),
      ),
    ]);
  } catch (e) {
    throw new Error(e.message);
  }
};

export const updateOnlineStatusOfLoginUser = async (user_id, status) => {
  try {
    const userDocRef = doc(db, collection_ref?.users, user_id?.toString());
    const docSnapshot = await getDoc(userDocRef);

    if (docSnapshot.exists()) {
      await updateDoc(userDocRef, { online: status });
    }
  } catch (e) {
    notification.error({
      description: e.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};
export const syncFirebaseGroup = async (group_id, group, newMessage = null) => {
  let groupMembers = [authUser?.id];
  let deletedStatuses = [];
  let blockedStatuses = [];
  let deletedMessagesId = [];
  let groupMessageStatuses = [];
  let readStatuses = [];
  const chatsCollectionRef = collection(db, collection_ref?.chats);
  const q = query(chatsCollectionRef, where("group_id", "==", group_id));
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach(async (dt) => {
    const documentRef = doc(db, collection_ref?.chats, dt.id);
    const docSnapshot = await getDoc(documentRef);
    const groupDoc = docSnapshot.data();
    if (newMessage) {
      await sendMessage({ chat_id: dt.id }, newMessage);
    }
    let groupUsers = groupDoc?.users_ids;
    group?.users.map((item) => {
      groupMembers.push(item.id);
    });
    /* update arrays */
    groupMembers?.map((item) => {
      let checkDeletedStatuses =
        groupDoc?.deleted_statuses?.filter((member) => member?.id == item) ||
        [];
      let checkBlockedStatuses =
        groupDoc?.blocked_statuses?.filter((member) => member?.id == item) ||
        [];
      let checkDeletedMessagesId =
        groupDoc?.deleted_messages_id?.filter((member) => member?.id == item) ||
        [];
      let checkGroupMessageStatuses =
        groupDoc?.group_message_statuses?.filter(
          (member) => member?.id == item,
        ) || [];
      let checkReadStatuses =
        groupDoc?.read_statuses?.filter((member) => member?.id == item) || [];
      if (checkDeletedStatuses.length > 0) {
        deletedStatuses.push(checkDeletedStatuses[0]);
      } else {
        deletedStatuses.push({ id: item, is_deleted: false });
      }
      if (checkBlockedStatuses.length > 0) {
        blockedStatuses.push(checkBlockedStatuses[0]);
      } else {
        blockedStatuses.push({ id: item, is_blocked: false });
      }
      if (checkDeletedMessagesId.length > 0) {
        deletedMessagesId.push(checkDeletedMessagesId[0]);
      } else {
        deletedMessagesId.push({ id: item, deleted_message_id: "" });
      }
      if (checkGroupMessageStatuses.length > 0) {
        groupMessageStatuses.push(checkGroupMessageStatuses[0]);
      } else {
        groupMessageStatuses.push({
          id: item,
          is_group_message_deleted: false,
        });
      }
      if (checkReadStatuses.length > 0) {
        readStatuses.push(checkReadStatuses[0]);
      } else {
        readStatuses.push({ id: item, is_read: false });
      }
    });

    const docRef = doc(db, collection_ref?.chats, dt.id);
    await updateDoc(docRef, {
      users_ids: groupMembers,
      blocked_statuses: blockedStatuses,
      deleted_messages_id: deletedMessagesId,
      deleted_statuses: deletedStatuses,
      group_message_statuses: groupMessageStatuses,
      read_statuses: readStatuses,
    });
  });
};
export const createGroupChat = async (
  data,
  user = null,
  group_id,
  dispatch,
  usersMembers,
  group,
) => {
  authUser = user || authUser;
  if (authUser) {
    try {
      if (!data?.users?.includes(authUser?.id)) {
        data?.users?.push(authUser?.id);
      }
      const chatRef = collection(db, collection_ref?.chats);
      await addDoc(chatRef, {
        salon_id: parseInt(authUser?.salon?.id),
        group_id: data?.group_id,
        admin_id: authUser?.id,
        blocked_statuses: data?.users?.map((item) => ({
          id: item,
          is_blocked: false,
        })),
        created_at: serverTimestamp(),
        deleted_messages_ids: data?.users?.map((item) => ({
          deleted_message_id: "",
          id: item,
          is_group_message_deleted: false,
        })),
        deleted_statuses: data?.users?.map((item) => ({
          id: item,
          is_deleted: false,
        })),
        group_message_statuses: data?.users?.map((item) => ({
          id: item,
          is_group_message_deleted: false,
        })),
        image: data?.image ? data?.image : "",
        is_group_chat: true,
        name: data?.name,
        last_message: "",
        read_statuses: data?.users?.map((item) => ({
          id: item,
          is_read: item == authUser?.id,
        })),
        un_read_count: 0,
        updated_at: serverTimestamp(),
        users_ids: data?.users?.map((item) => item),
      });
      if (group_id) {
        redirectToGroupChat(group_id, dispatch, usersMembers, group);
      }
    } catch (e) {
      notification.error({
        description: e.message,
        placement: "bottomRight",
        className: "createdNotification",
        closeIcon: <ToastCrossIcon />,
      });
    }
  }
};

export const updateGroupChat = async (data) => {
  try {
    const chatRef = collection(db, collection_ref?.chats);
    const q = query(chatRef, where("group_id", "==", data?.group_id));
    const querySnapshot = await getDocs(q);

    querySnapshot?.forEach(async (docSnapshot) => {
      const docRef = doc(db, collection_ref?.chats, docSnapshot.id);
      await updateDoc(docRef, {
        image: data?.image,
        name: data?.name,
      });
    });
  } catch (e) {
    notification.error({
      description: e.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};

export const deleteGroupChat = async (group_id) => {
  try {
    const chatRef = collection(db, collection_ref?.chats);
    const q = query(chatRef, where("group_id", "==", group_id));
    const querySnapshot = await getDocs(q);

    querySnapshot?.forEach(async (docSnapshot) => {
      const docRef = doc(db, collection_ref?.chats, docSnapshot.id);
      await deleteDoc(docRef);
    });
  } catch (e) {
    notification.error({
      description: e.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};

export const redirectToGroupChat = async (
  group_id,
  dispatch,
  usersMembers = [],
  group = null,
) => {
  usersMembers.push(authUser?.id);
  const usersCollectionRef = collection(db, collection_ref?.users);
  const chatsCollectionRef = collection(db, collection_ref?.chats);
  const q = query(chatsCollectionRef, where("group_id", "==", group_id));

  const querySnapshot = await getDocs(q);
  if (querySnapshot.size == 0) {
    let groupMembers = [];
    group?.users.map((item) => {
      groupMembers.push(item.id);
    });
    let data = {
      group_id,
      users: groupMembers,
      name: group?.name,
      image: group?.image ? group?.image : "",
    };
    await createGroupChat(data, null, group_id, dispatch, usersMembers, group);
  } else {
    querySnapshot.forEach(async (dt) => {
      if (dt.exists()) {
        const documentRef = doc(db, collection_ref?.chats, dt.id);
        const docSnapshot = await getDoc(documentRef);
        if (docSnapshot.exists()) {
          const documentData = docSnapshot.data();
          documentData.chat_id = dt.id;

          // fetch all users from collection
          const userquerySnapshot = await getDocs(usersCollectionRef);
          const users = [];
          userquerySnapshot.forEach((doc) => {
            const userData = doc.data();
            userData.id = doc.id;
            users.push(userData);
          });

          let profiles = [];

          // compare chats users_ids with users data and add relevent user as receiver_profile
          for (let i = 0; i < documentData?.users_ids?.length; i++) {
            const [receiver_profile] = users
              ? users.filter((item) => item?.id == documentData?.users_ids[i])
              : [];
            profiles.push(receiver_profile);
          }

          documentData.receiver_profile = profiles.map((profile) =>
            convertFirebaseTimestamps(profile),
          );

          dispatch(setSelectedUser(convertFirebaseTimestamps(documentData)));
        }
      }
    });
  }
};

export const signInToFirebase = () => {
  let token = GetTokenLocalStorage();
  fetch(`${constant.BASE_URL + apiUrl.firebase_token}`)
    .then((response) => response.json())
    .then((data) => {
      if (data?.data && typeof data?.data === "string") {
        signInWithCustomToken(auth, data.data)
          .then(function (result) {
            let user = result.user;
          })
          .catch(function (error) {
            let errorCode = error.code;
            let errorMessage = error.message;
            console.log(errorMessage, "err msg");
          });
      }
    })
    .catch((error) => {
      console.error("Error:", error);
    });
};
export const signInToFirebaseSync = async () => {
  try {
    const response = await fetch(
      `${constant.BASE_URL + apiUrl.firebase_token}`,
    );
    const data = await response.json();

    if (data?.data && typeof data?.data === "string") {
      await signInWithCustomToken(auth, data.data);
      console.log("User successfully signed in to Firebase");
    }
  } catch (error) {
    throw new Error(`Firebase sign-in error: ${error.message}`);
  }
};
export const deleteFirebaseUser = async (user_id) => {
  user_id = user_id.toString();
  const userDocRef = doc(db, collection_ref?.users, user_id);
  const userData = (await getDoc(userDocRef)).data();

  if (userData) {
    userData.is_deleted = true;
    userData.name = userData?.name;
    userData.image = userData?.image ? userData?.image : "";
    userData.position = userData?.position_id || null;
    await updateDoc(userDocRef, userData);
  }
};

export const addReaction = async (
  messageId,
  chatId,
  emoji,
  userId,
  dispatch,
  message,
) => {
  try {
    const currentReactions = message.reactions || {};
    const hasReaction = currentReactions[emoji]?.users?.[userId];
    const isAdding = !hasReaction;

    dispatch(setPendingReaction({ messageId, emoji, userId, isAdding }));

    const messageRef = doc(
      db,
      collection_ref?.chats,
      chatId,
      collection_ref?.threads,
      messageId,
    );

    await runTransaction(db, async (transaction) => {
      const messageDoc = await transaction.get(messageRef);

      if (!messageDoc.exists()) {
        throw new Error("Message does not exist!");
      }

      const messageData = messageDoc.data();
      const reactions = messageData.reactions || {};

      if (!reactions[emoji]) {
        reactions[emoji] = {
          count: 0,
          users: {},
        };
      }

      if (reactions[emoji].users[userId]) {
        delete reactions[emoji].users[userId];
        reactions[emoji].count = Math.max(0, reactions[emoji].count - 1);

        if (reactions[emoji].count === 0) {
          delete reactions[emoji];
        }
      } else {
        reactions[emoji].users[userId] = true;
        reactions[emoji].count = (reactions[emoji].count || 0) + 1;
      }

      transaction.update(messageRef, { reactions });
    });
  } catch (error) {
    dispatch(clearPendingReaction({ messageId, emoji, userId }));

    notification.error({
      description: error.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};

export const getReactionUsers = async (messageId, chatId, emoji, dispatch) => {
  try {
    const messageRef = doc(
      db,
      collection_ref?.chats,
      chatId,
      collection_ref?.threads,
      messageId,
    );
    const messageDoc = await getDoc(messageRef);

    if (messageDoc.exists()) {
      const messageData = messageDoc.data();
      const reactionData = messageData.reactions?.[emoji];

      if (reactionData?.users) {
        const userIds = Object.keys(reactionData.users);
        const users = [];

        // Fetch user details for each user who reacted
        for (const userId of userIds) {
          const userRef = doc(db, collection_ref?.users, userId);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            users.push(userDoc.data());
          }
        }

        dispatch(setReactionUsers({ messageId, emoji, users }));
        return users;
      }
    }

    return [];
  } catch (error) {
    notification.error({
      description: error.message,
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
    return [];
  }
};

export const deleteMessage = async (message, chatId, dispatch) => {
  try {
    if (!message?.thread_id || !chatId) {
      throw new Error("Invalid message or chat ID");
    }

    const messageRef = doc(
      db,
      collection_ref?.chats,
      chatId,
      collection_ref?.threads,
      message.thread_id,
    );

    await deleteDoc(messageRef);

    notification.success({
      description: "Message deleted successfully",
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  } catch (error) {
    console.error("Error deleting message:", error);
    notification.error({
      description: error.message || "Failed to delete message",
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};

export const updateMessage = async (message, chatId, newText, dispatch) => {
  try {
    if (!message?.thread_id || !chatId) {
      throw new Error("Invalid message or chat ID");
    }

    const messageRef = doc(
      db,
      collection_ref?.chats,
      chatId,
      collection_ref?.threads,
      message.thread_id,
    );

    await updateDoc(messageRef, {
      text: newText,
      edited: true,
      edited_at: serverTimestamp(),
    });

    notification.success({
      description: "Message updated successfully",
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  } catch (error) {
    console.error("Error updating message:", error);
    notification.error({
      description: error.message || "Failed to update message",
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
  }
};

export const addMembersToGroup = async (chatId, newMemberIds, dispatch) => {
  try {
    const chatRef = doc(db, collection_ref?.chats, chatId);
    const chatDoc = await getDoc(chatRef);

    if (!chatDoc.exists()) {
      throw new Error("Chat not found");
    }

    const deletedStatuses = newMemberIds.map((id) => ({
      id,
      is_deleted: false,
    }));

    const deletedMessagesIds = newMemberIds.map((id) => ({
      id,
      deleted_message_id: "",
    }));

    const readStatuses = newMemberIds.map((id) => ({
      id,
      is_read: true,
    }));

    const blockedStatuses = newMemberIds.map((id) => ({
      id,
      is_blocked: false,
    }));

    const groupMessageStatuses = newMemberIds.map((id) => ({
      id,
      is_group_message_deleted: false,
    }));

    await updateDoc(chatRef, {
      salon_id: parseInt(authUser?.salon?.id),
      users_ids: arrayUnion(...newMemberIds),
      deleted_statuses: arrayUnion(...deletedStatuses),
      deleted_messages_ids: arrayUnion(...deletedMessagesIds),
      read_statuses: arrayUnion(...readStatuses),
      blocked_statuses: arrayUnion(...blockedStatuses),
      group_message_statuses: arrayUnion(...groupMessageStatuses),
      updated_at: serverTimestamp(),
    });

    // Update group message deleted status for all members
    await updateGroupMessageDeleted(chatId);

    getChatUserList(dispatch, GetAuthUserLocalStorage()?.id);

    return true;
  } catch (error) {
    console.error("Error adding members:", error);
    notification.error({
      description: "Failed to add members to group",
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
    return false;
  }
};

export const removeMembersFromGroup = async (
  chatId,
  memberIdsToRemove,
  dispatch,
) => {
  try {
    const chatRef = doc(db, collection_ref?.chats, chatId);
    const chatDoc = await getDoc(chatRef);

    if (!chatDoc.exists()) {
      throw new Error("Chat not found");
    }

    const chatData = chatDoc.data();

    // Find status objects to remove for each member
    const deletedStatusesToRemove =
      chatData.deleted_statuses?.filter((status) =>
        memberIdsToRemove.includes(status.id),
      ) || [];

    const deletedMessagesIdsToRemove =
      chatData.deleted_messages_ids?.filter((status) =>
        memberIdsToRemove.includes(status.id),
      ) || [];

    const readStatusesToRemove =
      chatData.read_statuses?.filter((status) =>
        memberIdsToRemove.includes(status.id),
      ) || [];

    const blockedStatusesToRemove =
      chatData.blocked_statuses?.filter((status) =>
        memberIdsToRemove.includes(status.id),
      ) || [];

    const groupMessageStatusesToRemove =
      chatData.group_message_statuses?.filter((status) =>
        memberIdsToRemove.includes(status.id),
      ) || [];

    await updateDoc(chatRef, {
      salon_id: parseInt(authUser?.salon?.id),
      users_ids: arrayRemove(...memberIdsToRemove),
      deleted_statuses: arrayRemove(...deletedStatusesToRemove),
      deleted_messages_ids: arrayRemove(...deletedMessagesIdsToRemove),
      read_statuses: arrayRemove(...readStatusesToRemove),
      blocked_statuses: arrayRemove(...blockedStatusesToRemove),
      group_message_statuses: arrayRemove(...groupMessageStatusesToRemove),
      updated_at: serverTimestamp(),
    });

    // Update group message deleted status for remaining members
    await updateGroupMessageDeleted(chatId);
    getChatUserList(dispatch, GetAuthUserLocalStorage()?.id);

    return true;
  } catch (error) {
    console.error("Error removing members:", error);
    notification.error({
      description: "Failed to remove members from group",
      placement: "bottomRight",
      className: "createdNotification",
      closeIcon: <ToastCrossIcon />,
    });
    return false;
  }
};

const updateGroupMessageDeleted = async (documentId) => {
  try {
    const chatDoc = await getDoc(doc(db, collection_ref?.chats, documentId));

    if (!chatDoc.exists()) return;

    const chatData = chatDoc.data();
    const groupMessageStatuses =
      chatData.group_message_statuses?.map((status) => ({
        id: status.id,
        is_group_message_deleted: false,
      })) || [];

    await updateDoc(doc(db, collection_ref?.chats, documentId), {
      updated_at: serverTimestamp(),
      group_message_statuses: groupMessageStatuses,
    });
  } catch (error) {
    console.error("Error updating group message deleted status:", error);
  }
};

// Debounce timer for last open time updates
let lastOpenTimeDebounceTimer = null;

/**
 * Updates the last open time for a user in a specific chat
 * This is called when a user opens/clicks on a chat item
 * Uses debouncing to prevent excessive Firestore writes
 */
export const updateLastOpenTime = async (chatId, userId) => {
  if (!chatId || !userId) return;

  // Clear existing debounce timer
  if (lastOpenTimeDebounceTimer) {
    clearTimeout(lastOpenTimeDebounceTimer);
  }

  // Set new debounce timer (1 second delay)
  lastOpenTimeDebounceTimer = setTimeout(async () => {
    try {
      const chatDocRef = doc(db, collection_ref?.chats, chatId);

      // Use Firestore transaction to prevent race conditions
      await runTransaction(db, async (transaction) => {
        const chatDoc = await transaction.get(chatDocRef);

        if (!chatDoc.exists()) {
          throw new Error("Chat document does not exist");
        }

        const chatData = chatDoc.data();
        const currentTime = new Date(); // Use regular Date instead of serverTimestamp()

        // Get existing last_open_time array or create new one
        let lastOpenTimeArray = chatData.last_open_time || [];

        // Find existing entry for this user
        const existingUserIndex = lastOpenTimeArray.findIndex(
          (entry) => entry.id === userId,
        );

        if (existingUserIndex !== -1) {
          // Update existing entry
          lastOpenTimeArray[existingUserIndex] = {
            id: userId,
            last_open_time: currentTime,
          };
        } else {
          // Add new entry
          lastOpenTimeArray.push({
            id: userId,
            last_open_time: currentTime,
          });
        }

        // Update the document
        transaction.update(chatDocRef, {
          last_open_time: lastOpenTimeArray,
        });
      });
    } catch (error) {
      console.error("Error updating last open time:", error);
    }
  }, 1000); // 1 second debounce
};

/**
 * Calculates which users have seen a specific message
 * This is called dynamically to determine seen_members for each message
 */
export const calculateSeenMembers = (message, chatData) => {
  if (!message || !chatData?.last_open_time) {
    return [];
  }

  // Handle different timestamp formats
  let messageTime;
  if (message.created_at?.toDate) {
    // Firebase Timestamp object
    messageTime = message.created_at.toDate();
  } else if (message.created_at?.seconds) {
    // Firebase serverTimestamp object
    messageTime = new Date(message.created_at.seconds * 1000);
  } else if (typeof message.created_at === "number") {
    // Unix timestamp
    messageTime = new Date(message.created_at);
  } else {
    // Try to parse as date string
    messageTime = new Date(message.created_at);
  }

  // Validate the date
  if (isNaN(messageTime.getTime())) {
    console.warn("Invalid message timestamp:", message.created_at);
    return [];
  }

  const seenMembers = [];

  chatData.last_open_time.forEach((userOpenTime) => {
    let userOpenTimeDate;
    if (userOpenTime.last_open_time?.toDate) {
      // Firebase Timestamp object
      userOpenTimeDate = userOpenTime.last_open_time.toDate();
    } else if (userOpenTime.last_open_time?.seconds) {
      // Firebase serverTimestamp object
      userOpenTimeDate = new Date(userOpenTime.last_open_time.seconds * 1000);
    } else if (typeof userOpenTime.last_open_time === "number") {
      // Unix timestamp
      userOpenTimeDate = new Date(userOpenTime.last_open_time);
    } else {
      // Try to parse as date string
      userOpenTimeDate = new Date(userOpenTime.last_open_time);
    }

    // Validate the user open time date
    if (!isNaN(userOpenTimeDate.getTime())) {
      // If user opened the chat after the message was sent, they have seen it
      if (userOpenTimeDate >= messageTime) {
        seenMembers.push(userOpenTime.id);
      }
    }
  });

  return seenMembers;
};
